/**
 * 🏭 MOCK DASHBOARD GENERATOR
 * ════════════════════════════════════════════════════════════════════════
 *
 * Genera dashboards automáticamente desde un datasource mock.
 * Simula el comportamiento del backend (WidgetFactory, etc.)
 *
 * PROPÓSITO:
 * - Desarrollo de UX/UI sin backend
 * - Testing de generación de widgets
 * - Validación visual de layouts
 */

import type { Dashboard, DashboardWidget } from '../../types/dashboard';

interface DataRow {
  [key: string]: any;
}

interface MockDataSource {
  id: string;
  name: string;
  description: string;
  analysis: {
    intent: string;
    metrics: string[];
    dimensions: string[];
    timeDimensions: string[];
    primaryMetric: string;
  };
  rawSample: DataRow[];
}

const toNumber = (value: unknown) => (typeof value === 'number' ? value : 0);

const sumBy = (rows: DataRow[], field: string) =>
  rows.reduce((sum, row) => sum + toNumber(row[field]), 0);

const aggregateBy = (rows: DataRow[], key: string, valueField: string) => {
  const totals = rows.reduce((acc, row) => {
    const group = row[key] ?? 'Sin dato';
    acc[group] = (acc[group] || 0) + toNumber(row[valueField]);
    return acc;
  }, {} as Record<string, number>);

  return Object.entries(totals)
    .map(([categoria, valor]) => ({ categoria, valor }))
    .sort((a, b) => b.valor - a.valor);
};

const aggregateByMonth = (rows: DataRow[], valueField: string) => {
  const totals = rows.reduce((acc, row) => {
    const month = row.fecha?.substring(0, 7);
    if (!month) return acc;
    acc[month] = (acc[month] || 0) + toNumber(row[valueField]);
    return acc;
  }, {} as Record<string, number>);

  return Object.entries(totals)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fecha, valor]) => ({ fecha, valor }));
};

const averageByMonth = (rows: DataRow[], valueField: string) => {
  const totals = rows.reduce((acc, row) => {
    const month = row.fecha?.substring(0, 7);
    if (!month) return acc;
    const current = acc[month] || { total: 0, count: 0 };
    current.total += toNumber(row[valueField]);
    current.count += 1;
    acc[month] = current;
    return acc;
  }, {} as Record<string, { total: number; count: number }>);

  return Object.entries(totals)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fecha, info]) => ({ fecha, valor: info.count ? info.total / info.count : 0 }));
};

/**
 * Genera un dashboard automáticamente desde un datasource mock
 */
export function generateMockDashboard(datasource: MockDataSource): Dashboard {
  const { analysis, rawSample, name, description } = datasource;

  const ventas = rawSample.filter(row => row.tipo_operacion === 'Venta');
  const compras = rawSample.filter(row => row.tipo_operacion === 'Compra');

  // ════════════════════════════════════════════════════════════════
  // 1. CALCULAR TOTALES PARA KPIs
  // ════════════════════════════════════════════════════════════════
  const totalVentas = sumBy(ventas, 'importe');
  const totalCompras = sumBy(compras, 'importe');
  const margenBruto = totalVentas - totalCompras;
  const totalCantidad = sumBy(ventas, 'cantidad');
  const avgTicket = ventas.length ? totalVentas / ventas.length : 0;
  const clientesUnicos = new Set(ventas.map(r => r.cliente)).size;
  const proveedoresUnicos = new Set(compras.map(r => r.proveedor)).size;

  // ════════════════════════════════════════════════════════════════
  // 2. GENERAR WIDGETS AUTOMÁTICAMENTE
  // ════════════════════════════════════════════════════════════════
  const widgets: DashboardWidget[] = [];

  // Widget 1: Hero Metric - Ingresos de Ventas
  widgets.push({
    id: 'widget-hero-1',
    type: 'metric',
    role: 'PRIMARY_INSIGHT',
    title: 'Ingresos por Ventas',
    layout: { x: 0, y: 0, w: 12, h: 6 },
    metadata: {
      format: 'currency',
      decimals: 0,
      trend: '+18%',
      trendDirection: 'up',
      subtitle: 'vs campaña anterior',
      config: {
        title: 'Ingresos por Ventas (Campaña 25/26)'
      }
    },
    data: [{ valor: totalVentas }, { valor: totalVentas * 0.88 }]
  });

  // Widget 2: Compras estratégicas
  widgets.push({
    id: 'widget-kpi-compras',
    type: 'metric',
    role: 'AUXILIARY',
    title: 'Compras Estratégicas',
    layout: { x: 0, y: 6, w: 3, h: 3 },
    metadata: {
      format: 'currency',
      decimals: 0,
      trend: '+9%',
      trendDirection: 'up',
      config: {
        title: 'Compras Estratégicas (ytd)'
      }
    },
    data: [{ valor: totalCompras }, { valor: totalCompras * 0.91 }]
  });

  // Widget 3: Margen Bruto
  widgets.push({
    id: 'widget-kpi-margin',
    type: 'metric',
    role: 'AUXILIARY',
    title: 'Margen Bruto',
    layout: { x: 3, y: 6, w: 3, h: 3 },
    metadata: {
      format: 'currency',
      decimals: 0,
      trend: '+6%',
      trendDirection: 'up',
      config: {
        title: 'Margen Bruto Operativo'
      }
    },
    data: [{ valor: margenBruto }, { valor: margenBruto * 0.94 }]
  });

  // Widget 4: Clientes Activos
  widgets.push({
    id: 'widget-kpi-clientes',
    type: 'metric',
    role: 'AUXILIARY',
    title: 'Clientes Activos',
    layout: { x: 6, y: 6, w: 3, h: 3 },
    metadata: {
      format: 'number',
      decimals: 0,
      trend: '+12%',
      trendDirection: 'up',
      config: {
        title: 'Clientes Activos (90 días)'
      }
    },
    data: [{ valor: clientesUnicos }, { valor: Math.max(clientesUnicos - 2, 0) }]
  });

  // Widget 5: Proveedores Activos
  widgets.push({
    id: 'widget-kpi-proveedores',
    type: 'metric',
    role: 'AUXILIARY',
    title: 'Proveedores Activos',
    layout: { x: 9, y: 6, w: 3, h: 3 },
    metadata: {
      format: 'number',
      decimals: 0,
      trend: '+3%',
      trendDirection: 'up',
      config: {
        title: 'Proveedores Estratégicos'
      }
    },
    data: [{ valor: proveedoresUnicos }, { valor: Math.max(proveedoresUnicos - 1, 0) }]
  });

  // Widget 6: Toneladas Vendidas
  widgets.push({
    id: 'widget-kpi-toneladas',
    type: 'metric',
    role: 'AUXILIARY',
    title: 'Toneladas Vendidas',
    layout: { x: 0, y: 9, w: 3, h: 3 },
    metadata: {
      format: 'number',
      decimals: 0,
      trend: '+11%',
      trendDirection: 'up',
      config: {
        title: 'Toneladas Vendidas (insumos)'
      }
    },
    data: [{ valor: totalCantidad }, { valor: Math.max(totalCantidad * 0.9, 0) }]
  });

  // Widget 7: Ticket promedio
  widgets.push({
    id: 'widget-kpi-ticket',
    type: 'metric',
    role: 'AUXILIARY',
    title: 'Ticket Promedio',
    layout: { x: 3, y: 9, w: 3, h: 3 },
    metadata: {
      format: 'currency',
      decimals: 0,
      trend: '+4%',
      trendDirection: 'up',
      config: {
        title: 'Ticket Promedio de Venta'
      }
    },
    data: [{ valor: avgTicket }, { valor: avgTicket * 0.96 }]
  });

  // Widget 8: Top Productos (Bar)
  const topProductos = aggregateBy(ventas, 'producto', 'importe').slice(0, 8);
  widgets.push({
    id: 'widget-top-products',
    type: 'bar',
    role: 'DRIVER',
    title: 'Top Productos por Ingreso',
    layout: { x: 6, y: 9, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        xAxisLabel: 'Productos',
        yAxisLabel: 'Ingreso ($)',
        sortBy: 'value_desc',
        topN: 8
      }
    },
    data: topProductos
  });

  // Widget 9: Compras por proveedor
  const comprasProveedor = aggregateBy(compras, 'proveedor', 'importe');
  widgets.push({
    id: 'widget-compras-proveedor',
    type: 'bar',
    role: 'DRIVER',
    title: 'Compras por Proveedor',
    layout: { x: 0, y: 14, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        xAxisLabel: 'Proveedor',
        yAxisLabel: 'Monto ($)',
        sortBy: 'value_desc'
      }
    },
    data: comprasProveedor
  });

  // Widget 10: Ventas por región
  const ventasRegion = aggregateBy(ventas, 'region', 'importe');
  widgets.push({
    id: 'widget-sales-region',
    type: 'bar',
    role: 'DRIVER',
    title: 'Ventas por Región',
    layout: { x: 6, y: 14, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        xAxisLabel: 'Región',
        yAxisLabel: 'Ingreso ($)',
        sortBy: 'value_desc'
      }
    },
    data: ventasRegion
  });

  // Widget 11: Margen por categoría
  const ventasCategoria = aggregateBy(ventas, 'categoria', 'importe');
  const comprasCategoria = aggregateBy(compras, 'categoria', 'importe');
  const comprasByCategoria = comprasCategoria.reduce((acc, item) => {
    acc[item.categoria] = item.valor;
    return acc;
  }, {} as Record<string, number>);
  const margenCategoria = ventasCategoria.map((item) => ({
    categoria: item.categoria,
    valor: item.valor - (comprasByCategoria[item.categoria] || 0)
  }));

  widgets.push({
    id: 'widget-margin-category',
    type: 'bar',
    role: 'COMPARISON',
    title: 'Margen por Categoría',
    layout: { x: 0, y: 19, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        xAxisLabel: 'Categoría',
        yAxisLabel: 'Margen ($)',
        sortBy: 'value_desc'
      }
    },
    data: margenCategoria
  });

  // Widget 12: Mix de ventas por canal (Pie)
  const ventasCanal = aggregateBy(ventas, 'canal', 'importe');
  widgets.push({
    id: 'widget-sales-channel',
    type: 'pie',
    role: 'COMPARISON',
    title: 'Mix de Ventas por Canal',
    layout: { x: 6, y: 19, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        title: 'Mix de Ventas por Canal',
        sortBy: 'value_desc'
      }
    },
    data: ventasCanal
  });

  // Widget 13: Estado logístico (Pie)
  const ventasEstado = aggregateBy(ventas, 'estado', 'importe');
  widgets.push({
    id: 'widget-logistics-status',
    type: 'pie',
    role: 'COMPARISON',
    title: 'Estado Logístico de Órdenes',
    layout: { x: 0, y: 24, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        title: 'Estado Logístico de Órdenes'
      }
    },
    data: ventasEstado
  });

  // Widget 14: Tendencia Ventas Mensual (Line)
  const ventasMensuales = aggregateByMonth(ventas, 'importe');
  widgets.push({
    id: 'widget-trend-ventas',
    type: 'line',
    role: 'TREND',
    title: 'Tendencia de Ventas Mensual',
    layout: { x: 6, y: 24, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        xAxisLabel: 'Mes',
        yAxisLabel: 'Ventas ($)',
        showTrendline: true,
        lineColor: '#22c55e'
      }
    },
    data: ventasMensuales
  });

  // Widget 15: Tendencia Compras Mensual (Line)
  const comprasMensuales = aggregateByMonth(compras, 'importe');
  widgets.push({
    id: 'widget-trend-compras',
    type: 'line',
    role: 'TREND',
    title: 'Tendencia de Compras Mensual',
    layout: { x: 0, y: 29, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        xAxisLabel: 'Mes',
        yAxisLabel: 'Compras ($)',
        showTrendline: true,
        lineColor: '#f97316'
      }
    },
    data: comprasMensuales
  });

  // Widget 16: Precio promedio por mes (Line)
  const precioPromedio = averageByMonth(ventas, 'precio_unitario');
  widgets.push({
    id: 'widget-trend-precio',
    type: 'line',
    role: 'TREND',
    title: 'Precio Promedio Unitario',
    layout: { x: 6, y: 29, w: 6, h: 5 },
    metadata: {
      format: 'currency',
      decimals: 0,
      config: {
        xAxisLabel: 'Mes',
        yAxisLabel: 'Precio Unitario ($)',
        showTrendline: true,
        lineColor: '#38bdf8'
      }
    },
    data: precioPromedio
  });

  // Widget 17: Tabla de Transacciones
  widgets.push({
    id: 'widget-transactions',
    type: 'table',
    role: 'DETAIL',
    title: 'Últimas Transacciones',
    layout: { x: 0, y: 34, w: 12, h: 6 },
    metadata: {
      pageSize: 12,
      sortable: true,
      columns: [
        { key: 'fecha', label: 'Fecha', type: 'date' },
        { key: 'orden_id', label: 'Orden #', type: 'string' },
        { key: 'tipo_operacion', label: 'Tipo', type: 'string' },
        { key: 'cliente', label: 'Cliente', type: 'string' },
        { key: 'proveedor', label: 'Proveedor', type: 'string' },
        { key: 'producto', label: 'Producto', type: 'string' },
        { key: 'cantidad', label: 'Cant.', type: 'number' },
        { key: 'importe', label: 'Importe', type: 'currency' },
        { key: 'estado', label: 'Estado', type: 'badge' }
      ],
      config: {
        title: 'Detalle de Transacciones (venta + compra)'
      }
    },
    data: rawSample.slice(0, 40)
  });

  // ════════════════════════════════════════════════════════════════
  // 3. ENSAMBLAR DASHBOARD
  // ════════════════════════════════════════════════════════════════
  return {
    id: 'mock-dashboard-generated',
    title: `Dashboard de ${name}`,
    description: description || 'Dashboard generado automáticamente desde datos mock',
    intentType: analysis.intent,
    widgets,
    heroInsight: {
      metricName: 'Ingresos por Ventas',
      value: totalVentas,
      confidence: 'high',
      generatedBy: 'rules',
      period: 'Campaña 25/26',
      context: `${rawSample.length} transacciones analizadas`
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}
